import {useIntegrator} from "../../integrator.context";
import {RemoteConstantsModel, UnitData} from "../../models/remoteConstants.model";
import {useApp} from "../../app.context";
import AssetImage from "../../elements/assetImage.element";
import List from "../../elements/list";

interface CombatResultData {
    attacker: CombatUnitData;
    defender: CombatUnitData;
}

interface CombatantData {
    unit: UnitData;
    health: number;
    attack: number;
    defense: number;
}

interface CombatUnitData {
    id: string;
    formula: string;
    damageTaken: number;
    defeated: boolean;
    combatant: CombatantData;
}

function CombatSimulation() {
    const {state} = useIntegrator();
    const {getLocalizationValue} = useApp();

    return (
        // flex: 1 splits the General tab 50/50 with the settings column
        // (also flex: 1) instead of the old fixed 800px width.
        <div className={'layout vertical gap'} style={{flex: 1, minWidth: 300}}>
            <List title={'Simulations'}
                  elements={generateCombinations(state, state.units)}
                  component={element => <CombatResult result={element}/>}
                  defaultSearchKey={'Warrior'}
                  search={(element) => getLocalizationValue(element.attacker.id) + '_' +
                      getLocalizationValue(element.defender.id)}
                  keyFor={(element) => element.attacker.id + ':' + element.defender.id}
            />
        </div>
    )
}

function CombatResult(props: { result: CombatResultData }) {
    const {setNotification, getLocalizationValue} = useApp();

    function getFormulaMessage(): string {
        const attacker = props.result.attacker;
        const defender = props.result.defender;
        return `AA = ${attacker.combatant.attack}, AD = ${attacker.combatant.defense}, AH = ${attacker.combatant.health}\nDA = ${defender.combatant.attack}, DD = ${defender.combatant.defense}, DH = ${defender.combatant.health}\n\n${getLocalizationValue(attacker.id)} Damage: ${attacker.formula} = ${defender.damageTaken}\n${getLocalizationValue(defender.id)} Damage: ${defender.formula} = ${attacker.damageTaken}`;
    }

    return (
        <div className={'layout horizontal stretch center label'} style={{gap: 20}} onClick={() => {
            setNotification({
                title: 'Formula',
                message: getFormulaMessage()
            })
        }}>
            <CombatResultUnit unit={props.result.attacker}/>
            <div style={{backgroundColor: 'var(--overlay-color)', padding: 5, borderRadius: 'var(--border-radius)'}}>
                <span className={'fa-solid fa-swords'} style={{fontSize: 22, color: 'white'}}/>
            </div>
            <CombatResultUnit unit={props.result.defender}/>
        </div>
    )
}

function CombatResultUnit(props: {unit: CombatUnitData}) {
    const {getLocalizationValue} = useApp();

    const defeated = props.unit.defeated;
    const passive = props.unit.combatant.unit.attributes.includes('Passive');

    return (
        <div className={'layout horizontal center gap'} style={{width: 300, gap: 10}}>
            <AssetImage id={props.unit.id}/>
            <p className={'flex-grow'}>{getLocalizationValue(props.unit.id)}</p>
            {defeated && <span style={{color: 'white'}} className={'fa-solid fa-skull'}/>}
            {!defeated && passive && <span style={{color: 'white'}} className={'fa-solid fa-snooze'}/>}
            <p>{`${props.unit.combatant.health-props.unit.damageTaken}/${props.unit.combatant.health} (${-props.unit.damageTaken})`}</p>
        </div>
    )
}

function generateCombinations(constants: RemoteConstantsModel, units: UnitData[]): CombatResultData[] {
    const activeUnits = units.filter(unit => unit.active);
    const results: CombatResultData[] = [];

    for (let i = 0; i < activeUnits.length; i++) {
        for (let j = 0; j < activeUnits.length; j++) {
            const attackerUnit = activeUnits[i];
            if (attackerUnit.properties.attack <= 0) {
                continue;
            }

            const attacker: CombatantData = {
                unit: attackerUnit,
                health: attackerUnit.properties.health,
                attack: attackerUnit.properties.attack,
                defense: attackerUnit.properties.defense
            }

            const defenderUnit = activeUnits[j];
            const defender: CombatantData = {
                unit: defenderUnit,
                health: defenderUnit.properties.health,
                attack: defenderUnit.properties.attack,
                defense: defenderUnit.properties.defense
            }

            const attackingEvaluation = evaluateCombatDamage(constants, attacker, defender, true);

            const defenderDefeated = attackingEvaluation.damage >= defender.health;
            const isPassive = defenderUnit.attributes.includes('Passive');

            let defendingEvaluation: CombatEvaluation = { formula: '', damage: 0 };
            if (!defenderDefeated && !isPassive) {
                defendingEvaluation = evaluateCombatDamage(constants, defender, attacker, false);
            }

            const attackerDefeated = defendingEvaluation.damage >= attacker.health;

            results.push({
                attacker: {
                    id: attackerUnit.id,
                    formula: attackingEvaluation.formula,
                    damageTaken: defenderDefeated ? 0 : defendingEvaluation.damage,
                    defeated: !defenderDefeated && attackerDefeated,
                    combatant: attacker
                },
                defender: {
                    id: defenderUnit.id,
                    formula: defendingEvaluation.formula,
                    damageTaken: attackingEvaluation.damage,
                    defeated: defenderDefeated,
                    combatant: defender
                }
            });
        }
    }

    return results;
}

function evaluateCombatDamage(constants: RemoteConstantsModel, attacker: CombatantData, defender: CombatantData, attacking: boolean): CombatEvaluation {
    let formula = attacking ? constants.attackDamageFormula : constants.retaliatoryDamageFormula;

    const criticalPercent = (constants.criticalHealthPercent ?? 50) / 100;
    const criticalAttackPenalty = constants.criticalAttackPenalty ?? 1;
    const criticalDefensePenalty = constants.criticalDefensePenalty ?? 1;
    const lastStandAttr = constants.unitAttributes?.find(a => a.id === 'LastStand');
    const lastStandAttackBonus = lastStandAttr?.values?.['attackBonus'] ?? 1;
    const lastStandDefenseBonus = lastStandAttr?.values?.['defenseBonus'] ?? 1;

    const attackerIsCritical = attacker.health < attacker.health * criticalPercent;
    let attackerAttackBonus = 0;
    let attackerDefenseBonus = 0;
    if (attackerIsCritical) {
        if (attacker.unit.attributes.includes('LastStand')) {
            attackerAttackBonus = lastStandAttackBonus;
            attackerDefenseBonus = lastStandDefenseBonus;
        } else {
            attackerAttackBonus = -criticalAttackPenalty;
            attackerDefenseBonus = -criticalDefensePenalty;
        }
    }

    const defenderIsCritical = defender.health < defender.health * criticalPercent;
    let defenderAttackBonus = 0;
    let defenderDefenseBonus = 0;
    if (defenderIsCritical) {
        if (defender.unit.attributes.includes('LastStand')) {
            defenderAttackBonus = lastStandAttackBonus;
            defenderDefenseBonus = lastStandDefenseBonus;
        } else {
            defenderAttackBonus = -criticalAttackPenalty;
            defenderDefenseBonus = -criticalDefensePenalty;
        }
    }

    formula = formula.replace(/AA/g, `${attacker.attack + attackerAttackBonus}`);
    formula = formula.replace(/AD/g, `${attacker.defense + attackerDefenseBonus}`);
    formula = formula.replace(/AH/g, `${attacker.health}`);
    formula = formula.replace(/DA/g, `${defender.attack + defenderAttackBonus}`);
    formula = formula.replace(/DD/g, `${defender.defense + defenderDefenseBonus}`);
    formula = formula.replace(/DH/g, `${defender.health}`);

    let damage = 0;
    try {
        // eslint-disable-next-line no-eval
        damage = eval(formula);
    } catch (e) {}

    const minimumDamage = attacking ? (constants.minimumDamage ?? 1) : (constants.minimumRetaliatoryDamage ?? 1);
    return {
        formula: formula,
        damage: Math.max(Math.round(damage), minimumDamage)
    };
}

interface CombatEvaluation {
    formula: string;
    damage: number;
}

export default CombatSimulation;